import logger from '../utils/logger';
import ScheduleEntry from '../models/ScheduleEntry';

/**
 * UndoRedoManager handles undo/redo operations for schedule entries
 * Maintains separate stacks and integrates with change tracking
 */
class UndoRedoManager {
    constructor(maxStackSize = 50) {
        this.undoStack = [];
        this.redoStack = [];
        this.maxStackSize = maxStackSize;
        this.isUndoRedoInProgress = false; // Prevent recursive operations
    }

    /**
     * Record an action that can be undone
     * @param {string} action - 'create', 'modify', 'delete', 'move'
     * @param {*} beforeState - State before the action (null for create)
     * @param {*} afterState - State after the action (null for delete)
     * @param {string} itemId - ID of the affected item
     */
    recordAction(action, beforeState, afterState, itemId) {
        // Skip recording if we're in the middle of an undo/redo operation
        if (this.isUndoRedoInProgress) {
            return;
        }

        const actionRecord = {
            action,
            beforeState: beforeState ? this.cloneState(beforeState) : null,
            afterState: afterState ? this.cloneState(afterState) : null,
            itemId: String(itemId),
            timestamp: Date.now()
        };

        // Add to undo stack
        this.undoStack.push(actionRecord);
        
        // Clear redo stack since we have a new action
        this.redoStack = [];
        
        // Maintain max stack size
        if (this.undoStack.length > this.maxStackSize) {
            this.undoStack.shift(); // Remove oldest
        }

        logger.debug('[UndoRedoManager] Action recorded:', {
            action,
            itemId,
            undoStackSize: this.undoStack.length,
            redoStackSize: this.redoStack.length
        });
    }

    /**
     * Clone state to prevent reference issues
     */
    cloneState(state) {
        if (!state) return null;
        
        try {
            // Handle ScheduleEntry instances
            if (typeof state.toObject === 'function') {
                return state.toObject();
            }
            // Handle plain objects
            return JSON.parse(JSON.stringify(state));
        } catch (e) {
            logger.warn('[UndoRedoManager] Failed to clone state:', e);
            return { ...state };
        }
    }

    /**
     * Restore state from cloned data
     */
    restoreState(clonedState) {
        if (!clonedState) return null;
        
        try {
            return new ScheduleEntry(clonedState);
        } catch (e) {
            logger.warn('[UndoRedoManager] Failed to restore state:', e);
            return clonedState;
        }
    }

    /**
     * Perform undo operation
     * @param {Array} currentScheduledInstances - Current state
     * @param {Function} setScheduledInstances - State setter
     * @param {Function} addLogEntry - Change tracking function
     * @returns {boolean} - Whether undo was performed
     */
    undo(currentScheduledInstances, setScheduledInstances, addLogEntry) {
        if (this.undoStack.length === 0) {
            logger.debug('[UndoRedoManager] No actions to undo');
            return false;
        }

        const actionRecord = this.undoStack.pop();
        this.redoStack.push(actionRecord);
        this.isUndoRedoInProgress = true;

        try {
            const { action, beforeState, afterState, itemId } = actionRecord;
            
            logger.debug('[UndoRedoManager] Undoing action:', { action, itemId });

            let updatedInstances = [...currentScheduledInstances];
            const itemIndex = updatedInstances.findIndex(item => String(item.id) === String(itemId));

            switch (action) {
                case 'create':
                    // Undo create = delete the item
                    if (itemIndex !== -1) {
                        const removedItem = updatedInstances[itemIndex];
                        updatedInstances.splice(itemIndex, 1);
                        addLogEntry('delete', removedItem, null);
                    }
                    break;

                case 'delete':
                    // Undo delete = restore the item
                    if (beforeState) {
                        const restoredItem = this.restoreState(beforeState);
                        updatedInstances.push(restoredItem);
                        addLogEntry('create', null, restoredItem);
                    }
                    break;

                case 'modify':
                case 'move':
                    // Undo modify/move = restore previous state
                    if (beforeState && itemIndex !== -1) {
                        const restoredItem = this.restoreState(beforeState);
                        const oldItem = updatedInstances[itemIndex];
                        updatedInstances[itemIndex] = restoredItem;
                        addLogEntry('modify', oldItem, restoredItem);
                    }
                    break;

                default:
                    logger.warn('[UndoRedoManager] Unknown action type for undo:', action);
                    return false;
            }

            setScheduledInstances(updatedInstances);
            logger.debug('[UndoRedoManager] Undo completed:', { action, itemId });
            return true;

        } catch (error) {
            logger.error('[UndoRedoManager] Error during undo:', error);
            // Restore the action record if undo failed
            this.undoStack.push(actionRecord);
            this.redoStack.pop();
            return false;
        } finally {
            this.isUndoRedoInProgress = false;
        }
    }

    /**
     * Perform redo operation
     * @param {Array} currentScheduledInstances - Current state
     * @param {Function} setScheduledInstances - State setter
     * @param {Function} addLogEntry - Change tracking function
     * @returns {boolean} - Whether redo was performed
     */
    redo(currentScheduledInstances, setScheduledInstances, addLogEntry) {
        if (this.redoStack.length === 0) {
            logger.debug('[UndoRedoManager] No actions to redo');
            return false;
        }

        const actionRecord = this.redoStack.pop();
        this.undoStack.push(actionRecord);
        this.isUndoRedoInProgress = true;

        try {
            const { action, beforeState, afterState, itemId } = actionRecord;
            
            logger.debug('[UndoRedoManager] Redoing action:', { action, itemId });

            let updatedInstances = [...currentScheduledInstances];
            const itemIndex = updatedInstances.findIndex(item => String(item.id) === String(itemId));

            switch (action) {
                case 'create':
                    // Redo create = add the item back
                    if (afterState) {
                        const recreatedItem = this.restoreState(afterState);
                        updatedInstances.push(recreatedItem);
                        addLogEntry('create', null, recreatedItem);
                    }
                    break;

                case 'delete':
                    // Redo delete = remove the item again
                    if (itemIndex !== -1) {
                        const removedItem = updatedInstances[itemIndex];
                        updatedInstances.splice(itemIndex, 1);
                        addLogEntry('delete', removedItem, null);
                    }
                    break;

                case 'modify':
                case 'move':
                    // Redo modify/move = apply the change again
                    if (afterState && itemIndex !== -1) {
                        const modifiedItem = this.restoreState(afterState);
                        const oldItem = updatedInstances[itemIndex];
                        updatedInstances[itemIndex] = modifiedItem;
                        addLogEntry('modify', oldItem, modifiedItem);
                    }
                    break;

                default:
                    logger.warn('[UndoRedoManager] Unknown action type for redo:', action);
                    return false;
            }

            setScheduledInstances(updatedInstances);
            logger.debug('[UndoRedoManager] Redo completed:', { action, itemId });
            return true;

        } catch (error) {
            logger.error('[UndoRedoManager] Error during redo:', error);
            // Restore the action record if redo failed
            this.redoStack.push(actionRecord);
            this.undoStack.pop();
            return false;
        } finally {
            this.isUndoRedoInProgress = false;
        }
    }

    /**
     * Check if undo is available
     */
    canUndo() {
        return this.undoStack.length > 0;
    }

    /**
     * Check if redo is available
     */
    canRedo() {
        return this.redoStack.length > 0;
    }

    /**
     * Get stack sizes for debugging
     */
    getStackSizes() {
        return {
            undoStack: this.undoStack.length,
            redoStack: this.redoStack.length
        };
    }

    /**
     * Clear all stacks (use sparingly)
     */
    clear() {
        this.undoStack = [];
        this.redoStack = [];
        logger.debug('[UndoRedoManager] Stacks cleared');
    }
}

export default UndoRedoManager;
